// Owner: task-actions (docs/CONTRACTS.md "Task actions", owner feedback F14). Outcome-driven next actions of
// task types ("playbooks"). A task type can say what should happen when a task ends the way it ended, or
// reaches a state, or when another step is done: open a guided action (book a meeting, write a mail, create a
// PDF, ask for a signature, create or change a record, note a call, create follow-up tasks, start an
// automation, ask the agent). A step is *suggested* (one click in the task), *required* (before the task can
// be finished) or *automatic* (done for the person). Steps chain into playbooks (`after`).
//
// This module is browser-safe and holds the shapes: the library of action kinds with their typed parameters,
// the steps of a playbook, the rows that record what happened (`TaskActionRunDTO`), the stored library of
// reusable configured actions and the starter playbooks. The engine owns the data and the rules
// (`engine.taskActions`), `@dataroom/workflow` the executors (the same ones the automation step "Run action"
// uses), the web client the guided UIs and the visual editor.
import { z } from 'zod';
import { localizedDescription, localizedName } from './localized-storage';
import type { LocalizedText } from './localized-text';
import { isPlainRecord } from './json';

const KEY = /^[a-z][a-z0-9_]{0,62}$/;
const STEP_ID = /^[a-z][a-z0-9_]{0,40}$/;

// -- the library of action kinds -------------------------------------------------------------------------------

export const ACTION_KINDS = [
  'follow_up_task',
  'book_meeting',
  'send_mail',
  'generate_document',
  'request_signature',
  'create_record',
  'update_record',
  'log_note',
  'start_workflow',
  'notify',
  'ask_agent',
] as const;
export type ActionKind = (typeof ACTION_KINDS)[number];
export const isActionKind = (value: string): value is ActionKind =>
  (ACTION_KINDS as readonly string[]).includes(value);

export const ACTION_MODES = ['suggested', 'required', 'automatic'] as const;
export type ActionMode = (typeof ACTION_MODES)[number];
export const ACTION_TRIGGERS = ['outcome', 'created', 'state', 'after'] as const;
export type ActionTrigger = (typeof ACTION_TRIGGERS)[number];

export type ActionOption = { value: string; labelKey: string };
/** The typed editor of one parameter of an action (the web client draws them, the server validates them). */
export type ActionParamType =
  /** Text with `{{tokens}}`, typed against the playbook context (the task, its fields, the linked record). */
  | { kind: 'template'; long?: boolean }
  | { kind: 'integer'; min: number; max: number }
  | { kind: 'boolean' }
  | { kind: 'enum'; options: readonly ActionOption[] }
  | { kind: 'member' }
  | { kind: 'task_type'; /** An empty value means "the type of this task". */ same?: boolean }
  | { kind: 'meeting_type' }
  | { kind: 'pdf_template' }
  | { kind: 'mail_template' }
  | { kind: 'workflow' }
  | { kind: 'object'; /** Objects with a person trait only. */ person?: boolean }
  /** `{ field, value }` rows of the object named by the parameter `objectFrom`; values are templates. */
  | { kind: 'field_values'; objectFrom: string };

export type ActionParamDef = {
  key: string;
  type: ActionParamType;
  required?: boolean;
  default?: unknown;
  /** Shown only while another parameter has one of these values. */
  visibleWhen?: { param: string; equals: readonly string[] };
  /** Shown under "More options". */
  advanced?: boolean;
};

const opt = (kind: string, ...values: string[]): ActionOption[] =>
  values.map((value) => ({ value, labelKey: `ta.opt.${kind}.${value}` }));
const WHO = opt('who', 'task_person', 'linked_record');
const DUE_UNITS = opt('dueUnit', 'hours', 'days', 'business_days');

export type ActionKindInfo = {
  kind: ActionKind;
  icon: string;
  /** What the person does in the guided UI. `dialog`: a small form of the action itself; `app`: the product's own
   *  dialog (booking, mail composer, PDF layer); `agent`: the copilot. */
  guided: 'dialog' | 'app' | 'agent';
  /** Whether the action can run without a person (`automatic`). */
  automatic: boolean;
  /** Works on the record the task is about (a task without one cannot run it). */
  needsRecord: boolean;
  params: readonly ActionParamDef[];
};

export const ACTION_LIBRARY: Record<ActionKind, ActionKindInfo> = {
  follow_up_task: {
    kind: 'follow_up_task',
    icon: 'task',
    guided: 'dialog',
    automatic: true,
    needsRecord: false,
    params: [
      { key: 'title', type: { kind: 'template' }, required: true, default: '{{title}}' },
      { key: 'typeKey', type: { kind: 'task_type', same: true }, default: '' },
      {
        key: 'assignee',
        type: { kind: 'enum', options: opt('assignee', 'same', 'creator', 'member', 'type_default') },
        default: 'same',
      },
      { key: 'memberId', type: { kind: 'member' }, visibleWhen: { param: 'assignee', equals: ['member'] } },
      { key: 'dueAmount', type: { kind: 'integer', min: 0, max: 999 }, default: 2 },
      { key: 'dueUnit', type: { kind: 'enum', options: DUE_UNITS }, default: 'days' },
      {
        key: 'priority',
        type: { kind: 'enum', options: opt('priority', 'same', 'low', 'normal', 'high', 'urgent') },
        default: 'same',
      },
      { key: 'notes', type: { kind: 'template', long: true }, default: '' },
      { key: 'copyFields', type: { kind: 'boolean' }, default: true, advanced: true },
    ],
  },
  book_meeting: {
    kind: 'book_meeting',
    icon: 'calendar',
    guided: 'app',
    automatic: true,
    needsRecord: false,
    params: [
      { key: 'with', type: { kind: 'enum', options: WHO }, default: 'task_person' },
      { key: 'typeId', type: { kind: 'meeting_type' }, default: '' },
      { key: 'message', type: { kind: 'template', long: true }, default: '' },
      { key: 'proposeSlots', type: { kind: 'integer', min: 0, max: 8 }, default: 0, advanced: true },
      { key: 'validDays', type: { kind: 'integer', min: 1, max: 90 }, default: 14, advanced: true },
    ],
  },
  send_mail: {
    kind: 'send_mail',
    icon: 'mail',
    guided: 'app',
    automatic: true,
    needsRecord: false,
    params: [
      {
        key: 'to',
        type: { kind: 'enum', options: opt('to', 'task_person', 'linked_record', 'assignee', 'creator') },
        default: 'task_person',
      },
      { key: 'templateId', type: { kind: 'mail_template' }, default: '' },
      { key: 'subject', type: { kind: 'template' }, default: '' },
      { key: 'body', type: { kind: 'template', long: true }, default: '' },
    ],
  },
  generate_document: {
    kind: 'generate_document',
    icon: 'file',
    guided: 'app',
    automatic: true,
    needsRecord: true,
    params: [
      { key: 'templateId', type: { kind: 'pdf_template' }, required: true, default: '' },
      { key: 'finalize', type: { kind: 'boolean' }, default: true, advanced: true },
    ],
  },
  request_signature: {
    kind: 'request_signature',
    icon: 'shield',
    guided: 'app',
    automatic: true,
    needsRecord: true,
    params: [
      { key: 'templateId', type: { kind: 'pdf_template' }, required: true, default: '' },
      { key: 'signers', type: { kind: 'enum', options: WHO }, default: 'task_person' },
      { key: 'countersigner', type: { kind: 'member' }, advanced: true },
      { key: 'message', type: { kind: 'template', long: true }, default: '', advanced: true },
      { key: 'expiresInDays', type: { kind: 'integer', min: 1, max: 365 }, default: 30, advanced: true },
    ],
  },
  create_record: {
    kind: 'create_record',
    icon: 'plus',
    guided: 'dialog',
    automatic: true,
    needsRecord: false,
    params: [
      { key: 'objectKey', type: { kind: 'object' }, required: true },
      { key: 'values', type: { kind: 'field_values', objectFrom: 'objectKey' }, default: [] },
    ],
  },
  update_record: {
    kind: 'update_record',
    icon: 'pencil',
    guided: 'dialog',
    automatic: true,
    needsRecord: true,
    params: [
      { key: 'objectKey', type: { kind: 'object' }, required: true },
      { key: 'values', type: { kind: 'field_values', objectFrom: 'objectKey' }, default: [] },
    ],
  },
  log_note: {
    kind: 'log_note',
    icon: 'comment',
    guided: 'dialog',
    automatic: true,
    needsRecord: true,
    params: [
      { key: 'text', type: { kind: 'template', long: true }, required: true, default: '' },
      { key: 'activity', type: { kind: 'enum', options: opt('activity', 'note', 'call') }, default: 'note' },
    ],
  },
  start_workflow: {
    kind: 'start_workflow',
    icon: 'bolt',
    guided: 'dialog',
    automatic: true,
    needsRecord: false,
    params: [{ key: 'workflowId', type: { kind: 'workflow' }, required: true, default: '' }],
  },
  notify: {
    kind: 'notify',
    icon: 'bell',
    guided: 'dialog',
    automatic: true,
    needsRecord: false,
    params: [
      {
        key: 'to',
        type: { kind: 'enum', options: opt('notifyTo', 'assignee', 'creator', 'supervisor', 'member') },
        default: 'creator',
      },
      { key: 'memberId', type: { kind: 'member' }, visibleWhen: { param: 'to', equals: ['member'] } },
      { key: 'message', type: { kind: 'template', long: true }, required: true, default: '' },
    ],
  },
  ask_agent: {
    kind: 'ask_agent',
    icon: 'sparkle',
    guided: 'agent',
    automatic: false,
    needsRecord: false,
    params: [{ key: 'prompt', type: { kind: 'template', long: true }, required: true, default: '' }],
  },
};

/** The modes a kind offers: automatic only for kinds that can run without a person. */
export const modesFor = (kind: ActionKind): readonly ActionMode[] =>
  ACTION_LIBRARY[kind].automatic ? ACTION_MODES : ['suggested', 'required'];

/** The parameters with their defaults filled in (what a new step starts with). */
export function defaultActionParams(kind: ActionKind): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const p of ACTION_LIBRARY[kind].params) if (p.default !== undefined) out[p.key] = p.default;
  return out;
}

export type ActionParamProblem = { param: string; code: 'required' | 'invalid' | 'too_long' };

const isBlank = (v: unknown) =>
  v === undefined ||
  v === null ||
  (typeof v === 'string' && v.trim() === '') ||
  (Array.isArray(v) && v.length === 0);

/**
 * The problems of the parameters of an action: a required one that is empty, one of the wrong type or outside its
 * range, an option that does not exist. Templates and objects are checked further by the engine (typed against
 * the playbook context, the model, the stored objects). Hidden parameters are not checked.
 */
export function actionParamProblems(
  kind: ActionKind,
  params: Record<string, unknown>,
  mode: ActionMode = 'suggested',
): ActionParamProblem[] {
  const info = ACTION_LIBRARY[kind];
  const out: ActionParamProblem[] = [];
  const visible = (p: ActionParamDef) =>
    !p.visibleWhen || p.visibleWhen.equals.includes(String(params[p.visibleWhen.param] ?? ''));
  for (const p of info.params) {
    if (!visible(p)) continue;
    const v = params[p.key];
    const required =
      p.required ||
      (kind === 'follow_up_task' && p.key === 'memberId' && params.assignee === 'member') ||
      (kind === 'notify' && p.key === 'memberId' && params.to === 'member');
    if (isBlank(v)) {
      if (required && p.type.kind !== 'boolean') out.push({ param: p.key, code: 'required' });
      continue;
    }
    const t = p.type;
    if (t.kind === 'template' && (typeof v !== 'string' || v.length > 5000))
      out.push({ param: p.key, code: typeof v === 'string' ? 'too_long' : 'invalid' });
    else if (
      t.kind === 'integer' &&
      (typeof v !== 'number' || !Number.isInteger(v) || v < t.min || v > t.max)
    )
      out.push({ param: p.key, code: 'invalid' });
    else if (t.kind === 'boolean' && typeof v !== 'boolean') out.push({ param: p.key, code: 'invalid' });
    else if (t.kind === 'enum' && !t.options.some((o) => o.value === v))
      out.push({ param: p.key, code: 'invalid' });
    else if (
      ['member', 'meeting_type', 'pdf_template'].includes(t.kind) &&
      (typeof v !== 'string' || !/^[0-9a-f-]{36}$/i.test(v))
    )
      out.push({ param: p.key, code: 'invalid' });
    // An automation is named by its id or its key.
    else if (t.kind === 'workflow' && (typeof v !== 'string' || v.length > 120))
      out.push({ param: p.key, code: 'invalid' });
    else if (t.kind === 'object' && (typeof v !== 'string' || !KEY.test(v)))
      out.push({ param: p.key, code: 'invalid' });
    else if (t.kind === 'task_type' && (typeof v !== 'string' || (v !== '' && !KEY.test(v))))
      out.push({ param: p.key, code: 'invalid' });
    else if (t.kind === 'mail_template' && typeof v !== 'string') out.push({ param: p.key, code: 'invalid' });
    else if (t.kind === 'field_values') {
      const rows = Array.isArray(v) ? v : null;
      if (!rows || rows.length > 30) out.push({ param: p.key, code: 'invalid' });
      else
        for (const row of rows) {
          const r = isPlainRecord(row) ? row : null;
          if (
            !r ||
            typeof r.field !== 'string' ||
            !KEY.test(r.field) ||
            (r.value !== undefined && typeof r.value !== 'string')
          ) {
            out.push({ param: p.key, code: 'invalid' });
            break;
          }
        }
    }
  }
  // Automatic mail needs something to send.
  if (
    kind === 'send_mail' &&
    mode === 'automatic' &&
    isBlank(params.templateId) &&
    (isBlank(params.subject) || isBlank(params.body))
  )
    out.push({ param: isBlank(params.subject) ? 'subject' : 'body', code: 'required' });
  return out;
}

// -- steps of a playbook -----------------------------------------------------------------------------------------

/** What a step does: a kind of the library with its parameters, or a saved library entry (`library`). */
export const ActionRef = z.object({
  kind: z.enum(ACTION_KINDS),
  params: z.record(z.string(), z.unknown()).default({}),
  /** The key of a saved action of the workspace's library; when set its kind and parameters are used. */
  library: z.string().regex(KEY).nullable().default(null),
});
export type ActionRef = z.infer<typeof ActionRef>;

export const PlaybookStep = z
  .object({
    id: z.string().regex(STEP_ID),
    /** What the person reads on the card ("Book the meeting"), per language. */
    name: localizedName(80),
    /** `outcome`: when a task ends with one of `outcomes` (empty: any outcome). `created`: when a task of the type is
     * created. `state`: when it reaches one of `states`. `after`: when the step `after` is done. */
    trigger: z.enum(ACTION_TRIGGERS).default('outcome'),
    outcomes: z.array(z.string().regex(KEY)).max(8).default([]),
    states: z.array(z.string().regex(KEY)).max(12).default([]),
    after: z.string().regex(STEP_ID).nullable().default(null),
    /** A True/false expression over the task, its fields, the linked record (`record`) and `attempt`. Empty: always. */
    condition: z.string().max(1000).default(''),
    mode: z.enum(ACTION_MODES).default('suggested'),
    action: ActionRef,
    /** What the step is for, shown in the editor and on the card. */
    description: localizedDescription(300).default(''),
  })
  .superRefine((s, ctx) => {
    if (s.trigger === 'after' && !s.after)
      ctx.addIssue({ code: 'custom', path: ['after'], message: 'Choose the step it follows' });
    if (s.trigger === 'after' && s.mode === 'required')
      ctx.addIssue({
        code: 'custom',
        path: ['mode'],
        message: 'A step that follows another cannot be required',
      });
    if (!modesFor(s.action.kind).includes(s.mode))
      ctx.addIssue({ code: 'custom', path: ['mode'], message: 'This action cannot run without a person' });
  });
export type RawPlaybookStep = z.infer<typeof PlaybookStep>;

export const TaskPlaybook = z
  .object({
    steps: z.array(PlaybookStep).max(40).default([]),
    /** The object of the record the task is about: typed `record.<field>` in conditions and templates. */
    recordObject: z.string().regex(KEY).nullable().default(null),
  })
  .refine((p) => new Set(p.steps.map((s) => s.id)).size === p.steps.length, 'Step ids must be unique');
export type RawTaskPlaybook = z.infer<typeof TaskPlaybook>;
export const EMPTY_PLAYBOOK: RawTaskPlaybook = { steps: [], recordObject: null };

/** A playbook for a reader: names and descriptions in their language. */
export type TaskPlaybookDTO = Omit<RawTaskPlaybook, 'steps'> & {
  steps: Array<Omit<RawPlaybookStep, 'name' | 'description'> & { name: string; description: string }>;
};

/** Chains must not loop: a step follows a step that exists and does not (indirectly) follow it. */
export function playbookProblems(
  playbook: RawTaskPlaybook,
  outcomeKeys: readonly string[],
  stateKeys: readonly string[],
): string[] {
  const out: string[] = [];
  const ids = new Set(playbook.steps.map((s) => s.id));
  for (const s of playbook.steps) {
    if (s.trigger === 'outcome')
      for (const o of s.outcomes) if (!outcomeKeys.includes(o)) out.push(`${s.id}: unknown outcome "${o}"`);
    if (s.trigger === 'state')
      for (const k of s.states) if (!stateKeys.includes(k)) out.push(`${s.id}: unknown state "${k}"`);
    if (s.trigger === 'state' && !s.states.length) out.push(`${s.id}: choose a state`);
    if (s.trigger === 'after' && s.after && !ids.has(s.after)) out.push(`${s.id}: follows an unknown step`);
  }
  const follows = new Map(
    playbook.steps.filter((s) => s.trigger === 'after' && s.after).map((s) => [s.id, s.after!]),
  );
  for (const id of follows.keys()) {
    const seen = new Set<string>();
    let at: string | undefined = id;
    while (at && follows.has(at)) {
      if (seen.has(at)) {
        out.push(`${id}: the chain loops`);
        break;
      }
      seen.add(at);
      at = follows.get(at);
    }
  }
  return out;
}

/** The steps a task meets when it ends with `outcome` (the steps that follow them come after they are done). */
export function stepsForOutcome(playbook: RawTaskPlaybook, outcome: string | null): RawPlaybookStep[] {
  return playbook.steps.filter(
    (s) =>
      s.trigger === 'outcome' &&
      (s.outcomes.length === 0 || (outcome !== null && s.outcomes.includes(outcome))),
  );
}
export const stepsAfter = (playbook: RawTaskPlaybook, id: string): RawPlaybookStep[] =>
  playbook.steps.filter((s) => s.trigger === 'after' && s.after === id);

// -- the rows that record what happened ------------------------------------------------------------------------

export const ACTION_RUN_STATUSES = ['planned', 'pending', 'running', 'done', 'skipped', 'failed'] as const;
export type ActionRunStatus = (typeof ACTION_RUN_STATUSES)[number];

export type ActionTarget = {
  objectKey: string;
  recordId: string;
  title: string;
  email: string | null;
} | null;

export type TaskActionRunDTO = {
  id: string;
  taskId: string;
  stepId: string;
  /** The step's name in the reader's language. */
  name: string;
  description: string;
  kind: ActionKind;
  mode: ActionMode;
  trigger: ActionTrigger;
  outcome: string | null;
  outcomeLabel: string | null;
  status: ActionRunStatus;
  /** The library entry the step used. */
  library: string | null;
  /** The parameters with their templates rendered for this task (what the guided UI starts from). */
  params: Record<string, unknown>;
  /** The person or record the action is about (the task's person, the linked record). */
  target: ActionTarget;
  /** What the action produced: a task, a booking, a document, a record, a note. */
  result: ActionResult | null;
  error: string | null;
  attempts: number;
  createdAt: string;
  doneAt: string | null;
  doneBy: { id: string | null; name: string } | null;
  /** The run it follows in a chain. */
  afterRunId: string | null;
  /** Whether the viewer may do it (a member who may edit the task). */
  canAct: boolean;
  /** Why an action cannot be done now (no linked record, no person …). */
  blocked: string | null;
};

export type ActionResult = {
  /** A short sentence in the language the action ran in. */
  summary?: string;
  taskId?: string;
  recordId?: string;
  objectKey?: string;
  bookingId?: string;
  inviteId?: string;
  artifactId?: string;
  requestId?: string;
  mailId?: string;
  runId?: string;
  [key: string]: unknown;
};

/** The state of the next steps of a task (a chip in the Inbox, the header of the card). */
export type TaskNextSteps = {
  open: number;
  required: number;
  first: { id: string; name: string; kind: ActionKind; mode: ActionMode } | null;
};

/** What finishing a task with an outcome would set going (`POST /tasks/:id/playbook/prepare`). */
export type PlaybookPlanDTO = {
  outcome: string | null;
  /** Steps the person must do before the task can be finished (planned runs). */
  required: TaskActionRunDTO[];
  suggested: TaskActionRunDTO[];
  automatic: TaskActionRunDTO[];
  /** Required steps that are done: the task may be finished. */
  ready: boolean;
};

// -- the stored library of reusable actions ----------------------------------------------------------------------

export const LibraryAction = z.object({
  key: z.string().regex(KEY),
  name: localizedName(80),
  description: localizedDescription(300).default(''),
  kind: z.enum(ACTION_KINDS),
  params: z.record(z.string(), z.unknown()).default({}),
  archived: z.boolean().default(false),
});
export type RawLibraryAction = z.infer<typeof LibraryAction>;
export type LibraryActionDTO = Omit<RawLibraryAction, 'name' | 'description'> & {
  name: string;
  description: string;
  /** Where the action is used. */
  usage: {
    taskTypes: Array<{ key: string; name: string; steps: number }>;
    automations: Array<{ id: string; name: string }>;
  };
  createdAt: string;
  updatedAt: string;
};

// -- reporting -----------------------------------------------------------------------------------------------------

export type PlaybookStats = {
  typeKey: string;
  /** Tasks of the type that ended in the period. */
  closed: number;
  outcomes: Array<{
    outcome: string;
    label: string;
    tasks: number;
    /** Share of the closed tasks. */
    share: number;
    steps: Array<{
      stepId: string;
      name: string;
      kind: ActionKind;
      mode: ActionMode;
      offered: number;
      done: number;
      skipped: number;
      failed: number;
      open: number;
      /** done ÷ offered. */
      conversion: number;
      /** Median minutes from offered to done. */
      medianMinutes: number | null;
    }>;
    /** Follow-up tasks the outcome led to, and how many of them ended with a completing outcome. */
    followUps: { created: number; completed: number };
  }>;
};

// -- starter playbooks ----------------------------------------------------------------------------------------------

const L = (en: string, de: string): LocalizedText => ({ en, de });
type Starter = {
  key: string;
  name: LocalizedText;
  description: LocalizedText;
  /** Outcomes the playbook needs (added to the type when it lacks them). */
  outcomes: Array<{
    key: string;
    label: LocalizedText;
    tone: 'success' | 'danger' | 'warning' | 'neutral';
    status: 'completed' | 'cancelled';
    note: 'none' | 'optional' | 'required';
  }>;
  steps: RawPlaybookStep[];
};
const step = (
  s: Partial<RawPlaybookStep> & Pick<RawPlaybookStep, 'id' | 'name' | 'action'>,
): RawPlaybookStep => ({
  trigger: 'outcome',
  outcomes: [],
  states: [],
  after: null,
  condition: '',
  mode: 'suggested',
  description: '',
  ...s,
});

/**
 * Playbooks to start from. They only name the outcomes they need; a type keeps its own outcomes and gets the
 * missing ones added. Parameters that point to workspace data (a meeting type, a PDF template) stay empty: the
 * editor marks them and the person fills them in.
 */
export const PLAYBOOK_STARTERS: Starter[] = [
  {
    key: 'call_follow_up',
    name: L('Call follow-up', 'Anruf nachverfolgen'),
    description: L(
      'No answer: call again in two days. Meeting wanted: book it, then prepare what comes next. Not interested: note it.',
      'Nicht erreicht: in zwei Tagen erneut anrufen. Besprechung gewünscht: buchen, danach den nächsten Schritt vorbereiten. Kein Interesse: festhalten.',
    ),
    outcomes: [
      {
        key: 'reached',
        label: L('Reached', 'Erreicht'),
        tone: 'success',
        status: 'completed',
        note: 'optional',
      },
      {
        key: 'no_answer',
        label: L('No answer', 'Nicht erreicht'),
        tone: 'warning',
        status: 'completed',
        note: 'none',
      },
      {
        key: 'meeting_booked',
        label: L('Meeting wanted', 'Besprechung gewünscht'),
        tone: 'success',
        status: 'completed',
        note: 'none',
      },
      {
        key: 'not_interested',
        label: L('Not interested', 'Kein Interesse'),
        tone: 'danger',
        status: 'completed',
        note: 'required',
      },
    ],
    steps: [
      step({
        id: 'retry_call',
        name: L('Call again in 2 days', 'In 2 Tagen erneut anrufen'),
        outcomes: ['no_answer'],
        condition: 'attempt < 3',
        mode: 'automatic',
        action: {
          kind: 'follow_up_task',
          library: null,
          params: {
            title: '{{title}}',
            typeKey: '',
            assignee: 'same',
            dueAmount: 2,
            dueUnit: 'business_days',
            priority: 'same',
            notes: '',
            copyFields: true,
          },
        },
      }),
      step({
        id: 'book_meeting',
        name: L('Book the meeting', 'Besprechung buchen'),
        outcomes: ['meeting_booked'],
        mode: 'suggested',
        action: {
          kind: 'book_meeting',
          library: null,
          params: { with: 'task_person', typeId: '', message: '', proposeSlots: 0, validDays: 14 },
        },
      }),
      step({
        id: 'prepare_next',
        name: L('Prepare the next step', 'Nächsten Schritt vorbereiten'),
        trigger: 'after',
        after: 'book_meeting',
        mode: 'automatic',
        action: {
          kind: 'follow_up_task',
          library: null,
          params: {
            title: '{{title}}',
            typeKey: '',
            assignee: 'same',
            dueAmount: 1,
            dueUnit: 'business_days',
            priority: 'high',
            notes: '',
            copyFields: true,
          },
        },
      }),
      step({
        id: 'note_declined',
        name: L('Note why not', 'Absage festhalten'),
        outcomes: ['not_interested'],
        mode: 'suggested',
        action: { kind: 'log_note', library: null, params: { text: '{{result}}', activity: 'note' } },
      }),
    ],
  },
  {
    key: 'review_loop',
    name: L('Review and hand over', 'Prüfen und übergeben'),
    description: L(
      'Approved: tell the creator and create the next task. Rejected: ask the creator to rework it.',
      'Freigegeben: den Ersteller informieren und die nächste Aufgabe anlegen. Abgelehnt: Überarbeitung anfragen.',
    ),
    outcomes: [
      {
        key: 'approved',
        label: L('Approved', 'Freigegeben'),
        tone: 'success',
        status: 'completed',
        note: 'none',
      },
      {
        key: 'rejected',
        label: L('Rejected', 'Abgelehnt'),
        tone: 'danger',
        status: 'completed',
        note: 'required',
      },
    ],
    steps: [
      step({
        id: 'tell_creator',
        name: L('Tell the creator', 'Ersteller informieren'),
        outcomes: ['approved'],
        mode: 'automatic',
        action: { kind: 'notify', library: null, params: { to: 'creator', message: '{{title}}' } },
      }),
      step({
        id: 'rework',
        name: L('Ask for rework', 'Überarbeitung anfragen'),
        outcomes: ['rejected'],
        mode: 'required',
        action: {
          kind: 'follow_up_task',
          library: null,
          params: {
            title: '{{title}}',
            typeKey: '',
            assignee: 'creator',
            dueAmount: 2,
            dueUnit: 'business_days',
            priority: 'high',
            notes: '{{result}}',
            copyFields: true,
          },
        },
      }),
    ],
  },
];

/** The starter's steps and the outcomes it adds to a type that lacks them. */
export function applyStarter(
  starter: Starter,
  type: { outcomes: Array<{ key: string }> },
): { steps: RawPlaybookStep[]; addOutcomes: Starter['outcomes'] } {
  const have = new Set(type.outcomes.map((o) => o.key));
  return { steps: starter.steps, addOutcomes: starter.outcomes.filter((o) => !have.has(o.key)) };
}
